import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import EmptyState from "@/components/feature/EmptyState";
import { comicPages } from "@/mocks/comicPages";
import { getComicById } from "@/utils/comic";

type BgMode = "light" | "eye" | "dark";

const bgClass: Record<BgMode, string> = {
  light: "bg-background-50",
  eye: "bg-primary-50",
  dark: "bg-foreground-950",
};

const widthClass = ["max-w-2xl", "max-w-3xl", "max-w-4xl"];

const bgOptions: { id: BgMode; label: string; icon: string }[] = [
  { id: "light", label: "白天", icon: "ri-sun-line" },
  { id: "eye", label: "护眼", icon: "ri-leaf-line" },
  { id: "dark", label: "夜间", icon: "ri-moon-line" },
];

export default function ReadPage() {
  const { comicId, chapterId } = useParams();
  const navigate = useNavigate();
  const comic = getComicById(comicId);
  const [bgMode, setBgMode] = useState<BgMode>("light");
  const [widthIndex, setWidthIndex] = useState(1);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);

  const chapterIndex = useMemo(
    () => comic?.chapters.findIndex((c) => c.id === chapterId) ?? -1,
    [comic, chapterId]
  );

  useEffect(() => {
    setDrawerOpen(false);
    setSettingsOpen(false);
  }, [chapterId]);

  if (!comic || chapterIndex < 0) {
    return (
      <div className="w-full px-4 md:px-6 pt-24 md:pt-28 pb-16">
        <EmptyState
          icon="ri-file-warning-line"
          title="章节不存在或加载失败"
          description="该章节可能已被调整，你可以返回漫画详情页重新选择章节，或稍后重试。"
          action={
            <div className="flex flex-wrap items-center justify-center gap-3">
              <button
                type="button"
                onClick={() => window.location.reload()}
                className="inline-flex items-center gap-2 h-10 px-5 rounded-md border border-background-300 text-foreground-800 text-sm font-label font-bold cursor-pointer"
              >
                <i className="ri-refresh-line"></i>
                重试
              </button>
              <Link
                to={`/comic/${comicId ?? ""}`}
                className="inline-flex items-center gap-2 h-10 px-5 rounded-md bg-primary-500 text-background-50 text-sm font-label font-bold"
              >
                返回详情
              </Link>
            </div>
          }
        />
      </div>
    );
  }

  const chapter = comic.chapters[chapterIndex];
  const prevChapter = comic.chapters[chapterIndex - 1];
  const nextChapter = comic.chapters[chapterIndex + 1];
  const isDark = bgMode === "dark";

  const startIndex = (chapter.index * 2) % comicPages.length;
  const pages = Array.from(
    { length: 8 },
    (_, i) => comicPages[(startIndex + i) % comicPages.length]
  );

  const textMuted = isDark ? "text-background-300" : "text-foreground-600";
  const textMain = isDark ? "text-background-50" : "text-foreground-950";

  return (
    <div className={`min-h-screen ${bgClass[bgMode]}`}>
      <div className="pt-16">
        <div
          className={`sticky top-16 z-40 border-b ${
            isDark
              ? "bg-foreground-950/95 border-white/10"
              : "bg-background-50/95 border-background-200"
          } backdrop-blur`}
        >
          <div className="w-full px-4 md:px-6 h-14 flex items-center justify-between gap-3">
            <div className="flex items-center gap-3 min-w-0">
              <Link
                to={`/comic/${comic.id}`}
                className={`w-9 h-9 shrink-0 flex items-center justify-center rounded-md text-xl ${
                  isDark ? "text-background-100 hover:bg-white/10" : "text-foreground-700 hover:bg-background-100"
                } transition-colors`}
                aria-label="返回漫画详情"
              >
                <i className="ri-arrow-left-line"></i>
              </Link>
              <div className="min-w-0">
                <p className={`text-sm font-label font-bold truncate ${textMain}`}>
                  {comic.title}
                </p>
                <p className={`text-xs truncate ${textMuted}`}>
                  {chapter.title} · 第 {chapter.index} / {comic.chapters.length} 话
                </p>
              </div>
            </div>

            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => setSettingsOpen((v) => !v)}
                className={`hidden md:inline-flex items-center gap-1 h-9 px-4 rounded-md text-xs font-label font-bold transition-colors cursor-pointer ${
                  isDark ? "bg-white/10 text-background-100" : "bg-background-100 text-foreground-700"
                }`}
              >
                <i className="ri-settings-3-line"></i>
                阅读设置
              </button>
              <button
                type="button"
                onClick={() => setDrawerOpen(true)}
                className={`inline-flex items-center gap-1 h-9 px-4 rounded-md text-xs font-label font-bold transition-colors cursor-pointer ${
                  isDark ? "bg-white/10 text-background-100" : "bg-background-100 text-foreground-700"
                }`}
              >
                <i className="ri-list-check-2"></i>
                目录
              </button>
            </div>
          </div>

          {settingsOpen && (
            <div
              className={`border-t ${
                isDark ? "border-white/10" : "border-background-200"
              }`}
            >
              <div className="w-full px-4 md:px-6 py-4 flex flex-wrap items-center gap-6">
                <div className="flex items-center gap-2">
                  <span className={`text-xs font-label font-bold ${textMuted}`}>
                    背景
                  </span>
                  <div className="flex items-center gap-1 p-1 rounded-full bg-black/5">
                    {bgOptions.map((opt) => (
                      <button
                        key={opt.id}
                        type="button"
                        onClick={() => setBgMode(opt.id)}
                        className={`inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-label whitespace-nowrap transition-colors cursor-pointer ${
                          bgMode === opt.id
                            ? "bg-primary-500 text-background-50 font-bold"
                            : isDark
                              ? "text-background-200"
                              : "text-foreground-600"
                        }`}
                      >
                        <i className={opt.icon}></i>
                        {opt.label}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  <span className={`text-xs font-label font-bold ${textMuted}`}>
                    阅读宽度
                  </span>
                  <div className="flex items-center gap-1 p-1 rounded-full bg-black/5">
                    {["窄", "适中", "宽"].map((label, index) => (
                      <button
                        key={label}
                        type="button"
                        onClick={() => setWidthIndex(index)}
                        className={`px-3 py-1.5 rounded-full text-xs font-label whitespace-nowrap transition-colors cursor-pointer ${
                          widthIndex === index
                            ? "bg-primary-500 text-background-50 font-bold"
                            : isDark
                              ? "text-background-200"
                              : "text-foreground-600"
                        }`}
                      >
                        {label}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="w-full px-3 md:px-6 py-6 pb-28 md:pb-10">
          <div className={`mx-auto w-full ${widthClass[widthIndex]} flex flex-col gap-3`}>
            <div className={`text-center py-6 ${textMuted}`}>
              <p className="text-sm font-label">
                {chapter.title}
              </p>
              <p className="text-xs mt-1">发布于 {chapter.date}</p>
            </div>

            {pages.map((url, index) => (
              <div
                key={`${chapter.id}-${index}`}
                className="w-full aspect-[2/3] rounded-md overflow-hidden bg-background-100"
              >
                <img
                  src={url}
                  alt={`${comic.title} ${chapter.title} 第 ${index + 1} 页`}
                  title={`${comic.title} - 蛙漫在线漫画`}
                  className="w-full h-full object-cover object-top"
                />
              </div>
            ))}

            <div className={`text-center py-8 ${textMuted}`}>
              <p className="text-sm">— 本话结束 —</p>
              <p className="text-xs mt-1">感谢阅读，追更不迷路</p>
            </div>

            <div className="flex items-center justify-between gap-3">
              {prevChapter ? (
                <Link
                  to={`/read/${comic.id}/${prevChapter.id}`}
                  className="flex-1 inline-flex items-center justify-center gap-2 h-12 rounded-md bg-background-100 border border-background-200 text-sm font-label font-bold text-foreground-800 hover:border-primary-400 hover:text-primary-600 transition-colors"
                >
                  <i className="ri-arrow-left-line"></i>
                  上一章
                </Link>
              ) : (
                <span className="flex-1 inline-flex items-center justify-center gap-2 h-12 rounded-md bg-background-100 border border-background-200 text-sm font-label text-foreground-400 cursor-not-allowed">
                  已是第一章
                </span>
              )}
              {nextChapter ? (
                <Link
                  to={`/read/${comic.id}/${nextChapter.id}`}
                  className="flex-1 inline-flex items-center justify-center gap-2 h-12 rounded-md bg-primary-500 text-background-50 text-sm font-label font-bold hover:bg-primary-600 transition-colors"
                >
                  下一章
                  <i className="ri-arrow-right-line"></i>
                </Link>
              ) : (
                <Link
                  to={`/comic/${comic.id}`}
                  className="flex-1 inline-flex items-center justify-center gap-2 h-12 rounded-md bg-background-100 border border-background-200 text-sm font-label font-bold text-foreground-800 hover:border-primary-400 transition-colors"
                >
                  已是最新章 · 回详情
                </Link>
              )}
            </div>
          </div>
        </div>
      </div>

      {drawerOpen && (
        <div className="fixed inset-0 z-50 flex justify-end">
          <button
            type="button"
            aria-label="关闭目录"
            onClick={() => setDrawerOpen(false)}
            className="absolute inset-0 bg-black/40 cursor-default"
          ></button>
          <div className="relative w-[85%] max-w-sm h-full bg-background-50 flex flex-col animate-fade-in">
            <div className="h-16 shrink-0 px-4 flex items-center justify-between border-b border-background-200">
              <div>
                <p className="text-sm font-label font-bold text-foreground-950">
                  章节目录
                </p>
                <p className="text-xs text-foreground-500">
                  共 {comic.chapters.length} 话
                </p>
              </div>
              <button
                type="button"
                onClick={() => setDrawerOpen(false)}
                aria-label="关闭"
                className="w-9 h-9 flex items-center justify-center rounded-md text-foreground-600 hover:bg-background-100 text-xl cursor-pointer"
              >
                <i className="ri-close-line"></i>
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-3">
              <div className="flex flex-col gap-1">
                {comic.chapters.map((item) => (
                  <button
                    key={item.id}
                    type="button"
                    onClick={() => navigate(`/read/${comic.id}/${item.id}`)}
                    className={`text-left px-3 py-3 rounded-md text-sm transition-colors cursor-pointer ${
                      item.id === chapter.id
                        ? "bg-primary-500 text-background-50 font-bold"
                        : "text-foreground-800 hover:bg-background-100"
                    }`}
                  >
                    {item.title}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      <div
        className={`md:hidden fixed bottom-0 inset-x-0 z-40 border-t ${
          isDark
            ? "bg-foreground-950/95 border-white/10"
            : "bg-background-50/95 border-background-200"
        } backdrop-blur`}
      >
        <div className="px-3 py-2">
          {settingsOpen ? (
            <div className="flex flex-col gap-3 py-2">
              <div className="flex items-center gap-2">
                <span className={`text-xs font-label font-bold ${textMuted}`}>背景</span>
                <div className="flex items-center gap-1 p-1 rounded-full bg-black/5">
                  {bgOptions.map((opt) => (
                    <button
                      key={opt.id}
                      type="button"
                      onClick={() => setBgMode(opt.id)}
                      className={`inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-label whitespace-nowrap cursor-pointer ${
                        bgMode === opt.id
                          ? "bg-primary-500 text-background-50 font-bold"
                          : isDark
                            ? "text-background-200"
                            : "text-foreground-600"
                      }`}
                    >
                      <i className={opt.icon}></i>
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>
              <div className="flex items-center gap-2">
                <span className={`text-xs font-label font-bold ${textMuted}`}>宽度</span>
                <div className="flex items-center gap-1 p-1 rounded-full bg-black/5">
                  {["窄", "适中", "宽"].map((label, index) => (
                    <button
                      key={label}
                      type="button"
                      onClick={() => setWidthIndex(index)}
                      className={`px-3 py-1.5 rounded-full text-xs font-label whitespace-nowrap cursor-pointer ${
                        widthIndex === index
                          ? "bg-primary-500 text-background-50 font-bold"
                          : isDark
                            ? "text-background-200"
                            : "text-foreground-600"
                      }`}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          ) : (
            <div className="flex items-center gap-2">
              {prevChapter ? (
                <Link
                  to={`/read/${comic.id}/${prevChapter.id}`}
                  className={`flex-1 h-11 inline-flex items-center justify-center rounded-md text-sm font-label font-bold ${
                    isDark ? "bg-white/10 text-background-100" : "bg-background-100 text-foreground-800"
                  }`}
                >
                  上一章
                </Link>
              ) : (
                <span className="flex-1 h-11 inline-flex items-center justify-center rounded-md text-sm text-foreground-400 bg-background-100">
                  第一章
                </span>
              )}
              <button
                type="button"
                onClick={() => setSettingsOpen(true)}
                className={`w-11 h-11 shrink-0 inline-flex items-center justify-center rounded-md text-lg ${
                  isDark ? "bg-white/10 text-background-100" : "bg-secondary-100 text-secondary-800"
                }`}
                aria-label="阅读设置"
              >
                <i className="ri-settings-3-line"></i>
              </button>
              {nextChapter ? (
                <Link
                  to={`/read/${comic.id}/${nextChapter.id}`}
                  className="flex-1 h-11 inline-flex items-center justify-center rounded-md bg-primary-500 text-background-50 text-sm font-label font-bold"
                >
                  下一章
                </Link>
              ) : (
                <Link
                  to={`/comic/${comic.id}`}
                  className={`flex-1 h-11 inline-flex items-center justify-center rounded-md text-sm font-label font-bold ${
                    isDark ? "bg-white/10 text-background-100" : "bg-background-100 text-foreground-800"
                  }`}
                >
                  回详情
                </Link>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}